import React from 'react';
import { useNavigate } from 'react-router';
import { Formik } from 'formik';

import { ValidationCreateNews } from 'common/validations';
import PrimaryButton from 'components/form/buttons/primaryButton';
import OutlineButton from 'components/form/buttons/outLineButton';
import TextIput from 'components/form/textInput';
import FieldPair from 'components/form/fieldPair';
import PanelBoxLayout from 'components/layout/panelBox';
import { useActions } from 'common/hooks/useActions';
import UploadBox from 'components/form/uploadBox';
import TextArea from 'components/form/texArea';

import * as S from '../../styles';
import * as T from 'common/types';

interface IProps {
  data?: T.Pages.NewsListItem;
  closeModal?: () => void;
  fetchData?: () => void;
  loading: boolean;
}

/*
 * Two sections rather than one column of six controls with the image uploader
 * squashed into a flex row beside Reference — where it was labelled "Event
 * Cover Photo" on the news screen, and given a `height: 56` that its own
 * 220px box ignored.
 */
const NewsForm: React.FC<IProps> = ({ data, loading }) => {
  const nav = useNavigate();

  const { newsCreateRequestAction, newsUpdateRequestAction, newsUploadRequestAction, newsUpdateUploadRequestAction } =
    useActions();

  return (
    <Formik
      initialValues={{
        en_title: data?.newsTranslations?.find((t) => t.language === 'en')?.title || '',
        ar_title: data?.newsTranslations?.find((t) => t.language === 'ar')?.title || '',
        en_body: data?.newsTranslations?.find((t) => t.language === 'en')?.body || '',
        ar_body: data?.newsTranslations?.find((t) => t.language === 'ar')?.body || '',
        reference: data?.reference || '',
        image: data?.imageName || '',
        file: undefined,
        fileUrl: data?.image || ''
      }}
      enableReinitialize
      validationSchema={data?.id ? ValidationCreateNews : ValidationCreateNews}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        const newData = {
          reference: values.reference,
          translations: [
            {
              title: values.en_title,
              body: values.en_body,
              language: 'en'
            },
            {
              title: values.ar_title,
              body: values.ar_body,
              language: 'ar'
            }
          ]
        };
        if (data?.id)
          newsUpdateRequestAction({ ...newData, id: data?.id }, () => {
            resetForm();
            nav('/panel/news');
          });
        else
          newsCreateRequestAction({ ...newData, image: values.image }, () => {
            resetForm();
            nav('/panel/news');
          });
        setSubmitting(false);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setValues }) => (
        <S.Stack>
          <PanelBoxLayout title="Article">
            <S.Rows>
              <FieldPair>
                <TextIput
                  type="text"
                  name="en_title"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="News title"
                  label="Title"
                  langTag="EN"
                  required
                  value={values.en_title}
                  message={touched.en_title && (errors.en_title as any)}
                  error={touched.en_title && (errors.en_title as unknown as boolean)}
                />

                <TextIput
                  type="text"
                  name="ar_title"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="عنوان الخبر"
                  label="Title"
                  langTag="AR"
                  required
                  dir="rtl"
                  value={values.ar_title}
                  message={touched.ar_title && (errors.ar_title as any)}
                  error={touched.ar_title && (errors.ar_title as unknown as boolean)}
                />
              </FieldPair>

              <FieldPair>
                <TextArea
                  label="Body"
                  langTag="EN"
                  name="en_body"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="What happened?"
                  value={values.en_body}
                  message={touched.en_body && (errors.en_body as any)}
                  error={touched.en_body && (errors.en_body as unknown as boolean)}
                />

                <TextArea
                  label="Body"
                  langTag="AR"
                  dir="rtl"
                  name="ar_body"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="نص الخبر"
                  value={values.ar_body}
                  message={touched.ar_body && (errors.ar_body as any)}
                  error={touched.ar_body && (errors.ar_body as unknown as boolean)}
                />
              </FieldPair>
            </S.Rows>
          </PanelBoxLayout>

          <PanelBoxLayout title="Image and source">
            <S.Grid2>
              <S.Dropzone>
                <UploadBox
                  label="Image"
                  placeholder="Drop an image here, or click to choose one"
                  ratio="1.7 : 1"
                  onChange={(e: T.Form.FileUpload) => {
                    if (data?.id)
                      newsUpdateUploadRequestAction({ image: e?.file, id: data?.id }, (fileNameOnly: string) => {
                        setValues({
                          ...values,
                          image: fileNameOnly,
                          fileUrl: e.dataURL,
                          file: e?.file as any
                        });
                      });
                    else
                      newsUploadRequestAction({ image: e?.file }, (fileNameOnly: string) => {
                        setValues({
                          ...values,
                          image: fileNameOnly,
                          fileUrl: e.dataURL,
                          file: e?.file as any
                        });
                      });
                  }}
                  message={touched.file && (errors.file as any)}
                  error={touched.file && (errors.file as any)}
                  key={data?.id}
                  defaultImage={values.fileUrl}
                  value={{ file: values.file, dataURL: values.fileUrl }}
                />
              </S.Dropzone>

              <TextIput
                type="text"
                name="reference"
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Where the story came from"
                label="Reference"
                value={values.reference}
                message={touched.reference && (errors.reference as any)}
                error={touched.reference && (errors.reference as unknown as boolean)}
              />
            </S.Grid2>
          </PanelBoxLayout>

          <S.Actions>
            <S.Spacer />
            <OutlineButton link="/panel/news">Cancel</OutlineButton>
            <PrimaryButton onClick={handleSubmit} disabled={isSubmitting || loading}>
              {data?.id ? 'Save article' : 'Publish article'}
            </PrimaryButton>
          </S.Actions>
        </S.Stack>
      )}
    </Formik>
  );
};

export default NewsForm;
